import { useState, useRef, useEffect, useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import { supabase, chatDb, isSupabaseConfigured } from '../../lib/supabase'

interface Message {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: number
  provider?: string
  model?: string
}

type Provider = 'anthropic' | 'openai' | 'sandbox'
type Model = string

interface AIAssistantProps {
  context?: 'pipeline' | 'agents' | 'general'
  pendingQuery?: string | null
  onQueryConsumed?: () => void
}

const WS_URL = import.meta.env.VITE_WS_URL || 'ws://localhost:3001/ws'
const STORAGE_KEY = 'promptwizard-chat-history'
const SESSION_KEY = 'promptwizard-chat-session'

const CONTEXT_PROMPTS = {
  pipeline: 'You are helping design a multi-LLM orchestration pipeline. Help with task routing, model selection, error handling, and workflow design.',
  agents: 'You are helping design AI agents. Help with agent roles, capabilities, prompts, and coordination patterns.',
  general: 'You are an AI assistant for PromptWizard, a multi-LLM build orchestrator. Help with any questions about the system.'
}

const MODELS: Record<Provider, { id: string; name: string }[]> = {
  anthropic: [
    { id: 'claude-sonnet-4-20250514', name: 'Claude Sonnet 4' },
    { id: 'claude-3-5-haiku-20241022', name: 'Claude 3.5 Haiku' },
    { id: 'claude-3-5-sonnet-20241022', name: 'Claude 3.5 Sonnet' },
  ],
  openai: [
    { id: 'gpt-4o', name: 'GPT-4o' },
    { id: 'gpt-4o-mini', name: 'GPT-4o Mini' },
    { id: 'gpt-4-turbo', name: 'GPT-4 Turbo' },
  ],
  sandbox: [
    { id: 'claude-sonnet-4-20250514', name: 'Sandboxed Sonnet' },
  ]
}

const DEFAULT_MESSAGE: Message = {
  id: '0',
  role: 'system',
  content: 'AI Assistant ready. Ask me about pipelines, agents, or anything else.',
  timestamp: Date.now()
}

function loadLocalMessages(): Message[] {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      const parsed = JSON.parse(saved)
      if (Array.isArray(parsed) && parsed.length > 0) return parsed
    }
  } catch {}
  return [DEFAULT_MESSAGE]
}

export function AIAssistant({ context = 'general', pendingQuery, onQueryConsumed }: AIAssistantProps) {
  const [messages, setMessages] = useState<Message[]>(loadLocalMessages)
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [streamingContent, setStreamingContent] = useState('')
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected'>('connecting')
  const [provider, setProvider] = useState<Provider>('anthropic')
  const [model, setModel] = useState<Model>('claude-sonnet-4-20250514')
  const [showSettings, setShowSettings] = useState(false)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [userId, setUserId] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const streamingRef = useRef('')

  // Update model when provider changes
  useEffect(() => {
    setModel(MODELS[provider][0].id)
  }, [provider])

  // Initialize Supabase session
  useEffect(() => {
    if (!isSupabaseConfigured()) return

    const initSession = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return
      setUserId(user.id)

      const storedSessionId = localStorage.getItem(SESSION_KEY)
      if (storedSessionId) {
        const session = await chatDb.getSession(storedSessionId)
        if (session) {
          setSessionId(storedSessionId)
          const dbMessages = await chatDb.getMessages(storedSessionId)
          if (dbMessages.length > 0) {
            setMessages(dbMessages.map(m => ({
              id: m.id,
              role: m.role,
              content: m.content,
              timestamp: new Date(m.timestamp).getTime(),
              provider: m.provider || undefined,
              model: m.model || undefined
            })))
          }
          return
        }
      }

      const newSession = await chatDb.createSession(user.id, 'Chat Session')
      if (newSession) {
        setSessionId(newSession.id)
        localStorage.setItem(SESSION_KEY, newSession.id)
        await chatDb.addMessage(newSession.id, 'system', DEFAULT_MESSAGE.content)
      }
    }

    initSession()
  }, [])

  // Connect to WebSocket
  useEffect(() => {
    const connect = () => {
      setConnectionStatus('connecting')
      const ws = new WebSocket(WS_URL)
      wsRef.current = ws

      ws.onopen = () => {
        setConnectionStatus('connected')
      }

      ws.onmessage = async (event) => {
        try {
          const msg = JSON.parse(event.data)

          // Handle AI streaming
          if (msg.type === 'AI_STREAM') {
            streamingRef.current += msg.payload.text
            setStreamingContent(streamingRef.current)
          } else if (msg.type === 'AI_COMPLETE') {
            setIsLoading(false)
            const content = streamingRef.current.trim()
            if (content) {
              const newMsg: Message = {
                id: `${Date.now()}`,
                role: 'assistant' as const,
                content,
                timestamp: Date.now()
              }
              setMessages(prev => [...prev, newMsg])
              // Save to Supabase if configured
              const sid = localStorage.getItem(SESSION_KEY)
              if (isSupabaseConfigured() && sid) {
                chatDb.addMessage(sid, 'assistant', content, msg.payload?.provider, msg.payload?.model)
              }
            }
            streamingRef.current = ''
            setStreamingContent('')
          } else if (msg.type === 'AI_ERROR') {
            setIsLoading(false)
            const errorContent = `Error: ${msg.payload.error}`
            setMessages(prev => [...prev, {
              id: `${Date.now()}`,
              role: 'system' as const,
              content: errorContent,
              timestamp: Date.now()
            }])
            const sid = localStorage.getItem(SESSION_KEY)
            if (isSupabaseConfigured() && sid) {
              chatDb.addMessage(sid, 'system', errorContent)
            }
            streamingRef.current = ''
            setStreamingContent('')
          }
        } catch {
          // Ignore
        }
      }

      ws.onclose = () => {
        setConnectionStatus('disconnected')
        wsRef.current = null
        setTimeout(connect, 2000)
      }

      ws.onerror = () => {
        setConnectionStatus('disconnected')
      }
    }

    connect()
    return () => wsRef.current?.close()
  }, [])

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streamingContent])

  // Persist messages to localStorage (fallback when Supabase not configured)
  useEffect(() => {
    if (!isSupabaseConfigured()) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(messages))
      } catch {}
    }
  }, [messages])

  // Build chat messages for API (exclude system messages from UI)
  const buildChatMessages = useCallback((userContent: string) => {
    const chatMessages = messages
      .filter(m => m.role !== 'system')
      .map(m => ({ role: m.role, content: m.content }))
    chatMessages.push({ role: 'user', content: userContent })
    return chatMessages
  }, [messages])

  // Send message via API
  const sendMessageToAPI = useCallback((content: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return

    setIsLoading(true)
    streamingRef.current = ''
    setStreamingContent('')

    const chatMessages = buildChatMessages(content)

    wsRef.current.send(JSON.stringify({
      type: 'AI_CHAT',
      payload: {
        provider,
        model,
        systemPrompt: CONTEXT_PROMPTS[context],
        messages: chatMessages
      }
    }))
  }, [provider, model, context, buildChatMessages])

  // Handle pending query from AI inspector
  useEffect(() => {
    if (pendingQuery && !isLoading && connectionStatus === 'connected') {
      const userMessage: Message = {
        id: `${Date.now()}`,
        role: 'user',
        content: pendingQuery,
        timestamp: Date.now()
      }
      setMessages(prev => [...prev, userMessage])
      // Save user message to Supabase
      if (isSupabaseConfigured() && sessionId) {
        chatDb.addMessage(sessionId, 'user', pendingQuery, provider, model)
      }
      sendMessageToAPI(pendingQuery)
      onQueryConsumed?.()
    }
  }, [pendingQuery, isLoading, connectionStatus, sendMessageToAPI, onQueryConsumed, sessionId, provider, model])

  const sendMessage = useCallback(() => {
    const trimmed = input.trim()
    if (!trimmed || isLoading) return

    const userMessage: Message = {
      id: `${Date.now()}`,
      role: 'user',
      content: trimmed,
      timestamp: Date.now()
    }
    setMessages(prev => [...prev, userMessage])
    // Save user message to Supabase
    if (isSupabaseConfigured() && sessionId) {
      chatDb.addMessage(sessionId, 'user', trimmed, provider, model)
    }
    setInput('')
    sendMessageToAPI(trimmed)
  }, [input, isLoading, sendMessageToAPI, sessionId, provider, model])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const clearChat = async () => {
    const clearMsg = 'Chat cleared. How can I help?'
    const cleared: Message[] = [{
      id: '0',
      role: 'system' as const,
      content: clearMsg,
      timestamp: Date.now()
    }]
    setMessages(cleared)

    if (isSupabaseConfigured() && userId) {
      // Delete old session and create new one
      if (sessionId) {
        await chatDb.deleteSession(sessionId)
      }
      const newSession = await chatDb.createSession(userId, 'Chat Session')
      if (newSession) {
        setSessionId(newSession.id)
        localStorage.setItem(SESSION_KEY, newSession.id)
        await chatDb.addMessage(newSession.id, 'system', clearMsg)
      }
    } else {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cleared)) } catch {}
    }
  }

  const quickActions = [
    { label: 'Design Pipeline', prompt: 'Help me design a pipeline for processing Swift and TypeScript files with different LLM models' },
    { label: 'Create Agent', prompt: 'Help me create an agent definition for code review tasks' },
    { label: 'Error Handling', prompt: 'What error handling patterns should I use for my pipeline?' },
    { label: 'Optimize Routing', prompt: 'How can I optimize task routing between haiku, sonnet, and opus models?' }
  ]

  return (
    <div className="ai-assistant">
      <div className="ai-header">
        <div className="ai-title">
          <span className={`connection-dot ${connectionStatus}`} />
          <span className="ai-icon">◆</span>
          <span>AI Assistant</span>
        </div>
        <div className="ai-actions">
          <button
            className={`ai-action-btn ${showSettings ? 'active' : ''}`}
            onClick={() => setShowSettings(!showSettings)}
            title="Settings"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
              <circle cx="12" cy="12" r="3"/>
              <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/>
            </svg>
          </button>
          <button className="ai-action-btn" onClick={clearChat} title="Clear chat">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
              <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
            </svg>
          </button>
        </div>
      </div>

      {showSettings && (
        <div className="ai-settings">
          <div className="ai-setting">
            <label>Provider</label>
            <select value={provider} onChange={(e) => setProvider(e.target.value as Provider)}>
              <option value="anthropic">Anthropic (Claude)</option>
              <option value="openai">OpenAI (GPT)</option>
              <option value="sandbox">Sandbox Mode (Can create files)</option>
            </select>
          </div>
          <div className="ai-setting">
            <label>Model</label>
            <select value={model} onChange={(e) => setModel(e.target.value)}>
              {MODELS[provider].map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="ai-quick-actions">
        {quickActions.map((action, i) => (
          <button
            key={i}
            className="quick-action"
            onClick={() => {
              if (isLoading) return
              const userMessage: Message = {
                id: `${Date.now()}`,
                role: 'user',
                content: action.prompt,
                timestamp: Date.now()
              }
              setMessages(prev => [...prev, userMessage])
              if (isSupabaseConfigured() && sessionId) {
                chatDb.addMessage(sessionId, 'user', action.prompt, provider, model)
              }
              sendMessageToAPI(action.prompt)
            }}
            disabled={isLoading}
          >
            {action.label}
          </button>
        ))}
      </div>

      <div className="ai-messages">
        {messages.map(msg => (
          <div key={msg.id} className={`ai-message ${msg.role}`}>
            {msg.role === 'assistant' && (
              <span className="msg-avatar">{provider === 'sandbox' ? '⬡' : provider === 'anthropic' ? '◆' : '●'}</span>
            )}
            {msg.role === 'user' && (
              <span className="msg-avatar user">U</span>
            )}
            <div className="msg-content">
              <ReactMarkdown>{msg.content}</ReactMarkdown>
            </div>
          </div>
        ))}
        {streamingContent && (
          <div className="ai-message assistant streaming">
            <span className="msg-avatar">{provider === 'sandbox' ? '⬡' : provider === 'anthropic' ? '◆' : '●'}</span>
            <div className="msg-content"><ReactMarkdown>{streamingContent}</ReactMarkdown></div>
          </div>
        )}
        {isLoading && !streamingContent && (
          <div className="ai-message assistant">
            <span className="msg-avatar">{provider === 'sandbox' ? '⬡' : provider === 'anthropic' ? '◆' : '●'}</span>
            <div className="msg-content typing">
              <span className="dot"></span>
              <span className="dot"></span>
              <span className="dot"></span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="ai-input-area">
        <div className="ai-provider-badge" title={`Using ${provider === 'anthropic' ? 'Claude' : provider === 'sandbox' ? 'Sandboxed Claude' : 'GPT'}`}>
          {provider === 'sandbox' ? '⬡' : provider === 'anthropic' ? '◆' : '●'}
        </div>
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about pipelines, agents, or anything..."
          rows={2}
          disabled={isLoading}
        />
        <button
          className="ai-send-btn"
          onClick={sendMessage}
          disabled={!input.trim() || isLoading}
        >
          {isLoading ? (
            <span className="loading-spinner" />
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
              <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
            </svg>
          )}
        </button>
      </div>
    </div>
  )
}
